import { useContext } from 'react';
import { Check } from 'lucide-react';
import { Radio } from '@base-ui/react/radio';
import { RadioGroup } from '@/components/ui/radio-group';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';
import { conteoSr, textoConteo } from './facetas';
import { SeccionContexto } from './seccion-contexto';
import type { ChipOpcionProps, GrupoChipsProps, OpcionFiltro, UnidadConteo } from './tipos';

// Grupos del popover «Filtros» (pieza compartida, Ajuste #16; síntesis de la
// home §2 «Teclado y a11y»; artifact `.chips` / `.chip`; plan de migración a
// ReUI §3 R2 y Ajuste 28):
// - selección múltiple = `ToggleGroup multiple` de ui/, con `ToggleGroupItem`
//   por opción (no el `Toggle` crudo: roving tab stop y `aria-pressed` de la
//   pieza real de ui/, en vez de reimplementarlo);
// - selección única = `RadioGroup` de ui/ con `Radio.Root` crudo por opción
//   (el punto de 16 px de `RadioGroupItem` no aplica: acá la pastilla entera
//   es la opción, con la tilde en vez del punto).
// Las variantes `segmentado` y `lista` (Mostrar/Orden) se fueron: esas dos
// secciones pasaron a `TarjetasRadio` (Mostrar) y a un `RadioGroup` plano de
// ui/ (Orden, sin tarjeta — juicio del orquestador sobre el Hallazgo 1 de
// R2-QA: la tarjeta empujaba el popover fuera de 1440×694), las dos en
// `avivador/filtros-avivador-popover.tsx`. `GrupoChips` ya solo dibuja
// pastillas. `ChipOpcionProps.variante` (tipos.ts, sin tocar: lo consume la
// home) sigue en el contrato pero no se usa para elegir estilo — con una
// sola forma no hace falta.
// Nombre del grupo: `aria-labelledby` al título de la `FiltroSeccion`.
// Conteo: el visible es `aria-hidden` y el lector lee «Sin entregar, 21 de 41
// redes» (sr-only). Apagada = `text-tertiary`, nunca opacidad (AA). El anillo
// de foco es el único global (`:focus-visible` de index.css): ninguna clase
// de acá lo vuelve a dibujar.

// ---------------------------------------------------------------------------
// Clases de la pastilla (una sola forma: Ramas, Control RDT, Tipo)
// ---------------------------------------------------------------------------

/** Lo común a toda pastilla: `:active` 1 px abajo (motion-safe), nada de
 *  transiciones con reduced-motion. */
const CLASE_OPCION_BASE =
  'relative inline-flex cursor-pointer select-none items-center transition-[color,background-color,border-color,box-shadow,transform] duration-[120ms] ease-out motion-safe:active:translate-y-px motion-reduce:transition-none';

/** Forma de la pastilla (h-7, redondeada, texto 12.5 px). */
const CLASE_CHIP_FORMA = 'h-7 max-w-full gap-[5px] rounded-full border px-[11px] text-[12.5px] [@media(hover:none)]:min-h-11';

// El hover propio de la pastilla es SOLO el borde (`hover:border-border-strong`),
// nunca un relleno gris: en modo múltiple, `ui/toggle.tsx` trae de base
// `hover:bg-muted hover:text-foreground` (incondicional). Se neutraliza con el
// MISMO modificador (mismo truco que la elegida, más abajo) para que las
// pastillas de Ramas/Tipo (ToggleGroupItem) y las de Control RDT (Radio.Root
// crudo) se vean idénticas al pasar el mouse.
const CLASE_CHIP_NORMAL =
  'border-border-subtle bg-surface-card font-medium text-foreground hover:border-border-strong hover:bg-surface-card';
const CLASE_CHIP_APAGADA =
  'border-border-subtle bg-surface-card font-medium text-text-tertiary hover:border-border-strong hover:bg-surface-card hover:text-text-tertiary';

// La tilde tiene aire propio: 2 px menos a la izquierda compensan (y la
// pastilla crece menos al elegirla, así la fila no se parte).
/** Elegida, `Radio.Root` (modo único): el primitivo es crudo, sin clases
 *  propias que compitan — el color va sin modificador. */
const CLASE_CHIP_ELEGIDA_RADIO = 'border-primary/40 bg-primary/11 pl-[9px] font-semibold text-salta hover:border-primary/55';

/** Elegida, `ToggleGroupItem` (modo múltiple): `ui/toggle.tsx` ya trae
 *  `aria-pressed:bg-muted` (`toggleVariants`) — el color va con el MISMO
 *  modificador para que `cn()` (tailwind-merge) saque esa clase del string
 *  por grupo de conflicto, no para ganarle por especificidad CSS. El par
 *  `aria-pressed:hover:` cubre el hover de una pastilla ya elegida (si no,
 *  compite en igualdad de especificidad con el `hover:bg-muted` de la base). */
const CLASE_CHIP_ELEGIDA_TOGGLE =
  'aria-pressed:border-primary/40 aria-pressed:bg-primary/11 aria-pressed:pl-[9px] aria-pressed:font-semibold aria-pressed:text-salta aria-pressed:hover:border-primary/55 aria-pressed:hover:bg-primary/11 aria-pressed:hover:text-salta';

/** Contenedor del grupo de selección única. Sin margen de abajo: el aire
 *  entre grupos lo pone la `FiltroSeccion`. */
const CLASE_GRUPO_UNICO = 'flex w-full flex-wrap gap-1.5';

// ---------------------------------------------------------------------------
// GrupoChips
// ---------------------------------------------------------------------------

export function GrupoChips<V extends string>(props: GrupoChipsProps<V>) {
  const idSeccion = useContext(SeccionContexto);
  const labelledby = props.ariaLabelledby ?? idSeccion ?? undefined;
  const { opciones, unidad } = props;

  if (props.modo === 'multiple') {
    const { valor, onValorChange } = props;
    return (
      <ToggleGroup
        multiple
        value={valor}
        // En el ORDEN DE `opciones`, no en el de los clics (contrato), y sin
        // valores que no sean opciones.
        onValueChange={nuevo => onValorChange(opciones.filter(o => nuevo.includes(o.valor)).map(o => o.valor))}
        aria-labelledby={labelledby}
        // `spacing` (no un `gap-*` duplicado en className, Ajuste 28): es el
        // único que controla el gap real (`--gap`, ui/toggle-group.tsx) y el
        // `data-spacing` que leen los ítems.
        spacing={1.5}
        className="w-full flex-wrap"
      >
        {opciones.map(o => (
          <ChipOpcion
            key={o.valor}
            opcion={o}
            modo="multiple"
            variante="chips"
            seleccionada={valor.includes(o.valor)}
            unidad={unidad}
          />
        ))}
      </ToggleGroup>
    );
  }

  const { valor, onValorChange } = props;
  const variante = props.variante ?? 'chips';
  return (
    <RadioGroup
      // null = ninguna marcada (controlado igual: Base UI solo trata como
      // no controlado a `undefined`).
      value={valor}
      onValueChange={(v: unknown) => {
        const elegida = opciones.find(o => o.valor === v);
        if (elegida) onValorChange(elegida.valor);
      }}
      aria-labelledby={labelledby}
      className={CLASE_GRUPO_UNICO}
    >
      {opciones.map(o => (
        <ChipOpcion
          key={o.valor}
          opcion={o}
          modo="unico"
          variante={variante}
          seleccionada={valor === o.valor}
          unidad={unidad}
        />
      ))}
    </RadioGroup>
  );
}

// ---------------------------------------------------------------------------
// ChipOpcion
// ---------------------------------------------------------------------------

/** Una opción. Va SIEMPRE adentro de un `GrupoChips` (el Toggle necesita su
 *  `ToggleGroup` y el radio su `RadioGroup`). `variante` sigue en el contrato
 *  (`ChipOpcionProps`, tipos.ts) pero ya no elige estilo: con una sola forma
 *  de pastilla no hace falta. */
export function ChipOpcion<V extends string>({ opcion, modo, seleccionada, unidad }: ChipOpcionProps<V>) {
  // Una opción elegida nunca se pinta apagada. Apagada no es deshabilitada.
  const apagada = !seleccionada && (opcion.apagada ?? opcion.conteo === 0);
  const contenido = <ContenidoOpcion opcion={opcion} seleccionada={seleccionada} unidad={unidad} />;

  if (modo === 'multiple') {
    return (
      <ToggleGroupItem
        value={opcion.valor}
        className={cn(
          CLASE_OPCION_BASE,
          CLASE_CHIP_FORMA,
          seleccionada ? CLASE_CHIP_ELEGIDA_TOGGLE : apagada ? CLASE_CHIP_APAGADA : CLASE_CHIP_NORMAL,
        )}
      >
        {contenido}
      </ToggleGroupItem>
    );
  }
  return (
    <Radio.Root
      value={opcion.valor}
      className={cn(
        CLASE_OPCION_BASE,
        CLASE_CHIP_FORMA,
        seleccionada ? CLASE_CHIP_ELEGIDA_RADIO : apagada ? CLASE_CHIP_APAGADA : CLASE_CHIP_NORMAL,
      )}
    >
      {contenido}
    </Radio.Root>
  );
}

function ContenidoOpcion<V extends string>({
  opcion,
  seleccionada,
  unidad,
}: {
  opcion: OpcionFiltro<V>;
  seleccionada: boolean;
  unidad?: UnidadConteo;
}) {
  const conteo =
    opcion.conteo === undefined ? null : (
      <Conteo conteo={opcion.conteo} total={opcion.total} unidad={unidad} seleccionada={seleccionada} />
    );

  return (
    <>
      {seleccionada && <Check aria-hidden="true" className="size-[11px] flex-none" strokeWidth={2.4} />}
      <span className="min-w-0 truncate">{opcion.etiqueta}</span>
      {conteo}
    </>
  );
}

/** Conteo visible («21/41», `aria-hidden`) + el del lector («, 21 de 41 redes»). */
function Conteo({
  conteo,
  total,
  unidad,
  seleccionada,
}: {
  conteo: number;
  total?: number;
  unidad?: UnidadConteo;
  seleccionada: boolean;
}) {
  // En la pastilla elegida el número va en salta; en el resto, terciario
  // (artifact `.chip.on .n`).
  const color = seleccionada ? 'font-medium text-salta' : 'font-normal text-text-tertiary';
  return (
    <>
      <span aria-hidden="true" className={cn('flex-none tabular-nums text-[11.5px]', color)}>
        {textoConteo(conteo, total)}
      </span>
      <span className="sr-only">, {conteoSr(conteo, total, unidad)}</span>
    </>
  );
}
